import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider, useAuth } from './lib/auth'
import { BuildBadge } from './components/BuildBadge'
import { AnalyticsGate } from './components/ConsentBanner'
import { ProtectedRoute, AdminRoute } from './components/ProtectedRoute'
import Login from './pages/Login'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Home from './pages/marketing/Home'
import Features from './pages/marketing/Features'
import Pricing from './pages/marketing/Pricing'
import About from './pages/marketing/About'
import Integrations from './pages/marketing/Integrations'
import Contact from './pages/marketing/Contact'

// Everything reachable only after login is lazy -- a marketing visitor
// (the one audience that actually needs a fast first paint; see
// entry-server.tsx's PRERENDER_ROUTES) was downloading the entire
// logged-in app, unused, as part of one bundle. Found via a PageSpeed
// audit (2026-10-09): 114 KiB of a 180 KiB bundle (65%) was unused on
// the homepage. Everything above this line stays a normal eager import
// (public marketing pages plus the signup/login funnel) since none of
// it is large enough individually to be worth the added complexity, and
// Login/Register sit on the business-critical signup path.
const Register = lazy(() => import('./pages/Register'))
const RegisterBusinessSetup = lazy(() => import('./pages/RegisterBusinessSetup'))
const RegisterBilling = lazy(() => import('./pages/RegisterBilling'))
const BillingCallback = lazy(() => import('./pages/BillingCallback'))
const OperatorAgreement = lazy(() => import('./pages/OperatorAgreement'))
const Join = lazy(() => import('./pages/Join'))
const Forgot = lazy(() => import('./pages/Forgot'))
const Reset = lazy(() => import('./pages/Reset'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Help = lazy(() => import('./pages/Help'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Practice = lazy(() => import('./pages/Practice'))
const PracticeQueue = lazy(() => import('./pages/PracticeQueue'))
const PracticeTeam = lazy(() => import('./pages/PracticeTeam'))
const PracticeReport = lazy(() => import('./pages/PracticeReport'))
const AdminPractices = lazy(() => import('./pages/admin/Practices'))
const Reminders = lazy(() => import('./pages/Reminders'))
const Customer = lazy(() => import('./pages/Customer'))
const OutstandingContacts = lazy(() => import('./pages/OutstandingContacts'))
const Rules = lazy(() => import('./pages/Rules'))
const MessageTemplates = lazy(() => import('./pages/MessageTemplates'))
const Connections = lazy(() => import('./pages/Connections'))
const Settings = lazy(() => import('./pages/Settings'))
const AdminConsole = lazy(() => import('./pages/admin/AdminConsole'))
const Customers = lazy(() => import('./pages/admin/Customers'))
const ContactInquiries = lazy(() => import('./pages/admin/ContactInquiries'))
const FeedbackAdmin = lazy(() => import('./pages/admin/Feedback'))
const Waitlist = lazy(() => import('./pages/admin/Waitlist'))
const AdminActions = lazy(() => import('./pages/admin/AdminActions'))
const BillingOverview = lazy(() => import('./pages/admin/BillingOverview'))
const AdminAnalyticsOverview = lazy(() => import('./pages/admin/AnalyticsOverview'))
const MessagingTestSend = lazy(() => import('./pages/admin/MessagingTestSend'))
const IntegrationLogsPage = lazy(() => import('./pages/admin/IntegrationLogs'))
const SendPlanPage = lazy(() => import('./pages/admin/SendPlan'))
const WhatsAppTemplateSubmissionsPage = lazy(() => import('./pages/admin/WhatsAppTemplateSubmissions'))
const WhatsAppPlatformTemplatesPage = lazy(() => import('./pages/admin/WhatsAppPlatformTemplates'))
const ChannelsPage = lazy(() => import('./pages/admin/Channels'))
const VersionsPage = lazy(() => import('./pages/admin/Versions'))
const WhatsAppNumbersPage = lazy(() => import('./pages/admin/WhatsAppNumbers'))
const RemindersTodayPage = lazy(() => import('./pages/admin/RemindersToday'))
const ScheduledJobsSettingsPage = lazy(() => import('./pages/admin/ScheduledJobsSettings'))
const PlansPricingSettingsPage = lazy(() => import('./pages/admin/PlansPricingSettings'))
const ConfigAuditPage = lazy(() => import('./pages/admin/ConfigAudit'))

// A mistyped URL must not drop a logged-in customer on the marketing home.
function NotFound() {
  const { user } = useAuth()
  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="not-found">
      <h1 className="text-h2 text-foreground">This page does not exist</h1>
      <p className="mt-3 text-muted-foreground">Check the address, or go back.</p>
      <p className="mt-6">
        <a className="font-semibold text-primary underline" href={user ? '/dashboard' : '/login'}>
          {user ? 'Go to your dashboard' : 'Log in'}
        </a>
      </p>
    </div>
  )
}

// Same loading markup ProtectedRoute/AdminRoute already use for their own
// isLoading state, so a lazy chunk's download and an auth check read as
// the same "still loading" moment rather than two different spinners.
function RouteFallback() {
  return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 10_000 } },
})

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/join/:token" element={<Join />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset/:token" element={<Reset />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/register/business-setup"
              element={
                <ProtectedRoute>
                  <RegisterBusinessSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/register/billing"
              element={
                <ProtectedRoute>
                  <RegisterBilling />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/callback"
              element={
                <ProtectedRoute>
                  <BillingCallback />
                </ProtectedRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/operator-agreement" element={<OperatorAgreement />} />
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <Reminders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/:topic"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:client_id"
              element={
                <ProtectedRoute>
                  <Customer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/outstanding-contacts"
              element={
                <ProtectedRoute>
                  <OutstandingContacts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rules"
              element={
                <ProtectedRoute>
                  <Rules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/message-templates"
              element={
                <AdminRoute>
                  <MessageTemplates />
                </AdminRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute>
                  <Connections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practice"
              element={
                <ProtectedRoute>
                  <Practice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practice/queue"
              element={
                <ProtectedRoute>
                  <PracticeQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practice/team"
              element={
                <ProtectedRoute>
                  <PracticeTeam />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practice/clients/:orgId/report"
              element={
                <ProtectedRoute>
                  <PracticeReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/practices"
              element={
                <AdminRoute>
                  <AdminPractices />
                </AdminRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminConsole />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <AdminRoute>
                  <Customers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contact-inquiries"
              element={
                <AdminRoute>
                  <ContactInquiries />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/waitlist"
              element={
                <AdminRoute>
                  <Waitlist />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <AdminRoute>
                  <FeedbackAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/actions"
              element={
                <AdminRoute>
                  <AdminActions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/billing"
              element={
                <AdminRoute>
                  <BillingOverview />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <AdminRoute>
                  <AdminAnalyticsOverview />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/messaging"
              element={
                <AdminRoute>
                  <MessagingTestSend />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/integration-logs"
              element={
                <AdminRoute>
                  <IntegrationLogsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/send-plan"
              element={
                <AdminRoute>
                  <SendPlanPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whatsapp-template-submissions"
              element={
                <AdminRoute>
                  <WhatsAppTemplateSubmissionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whatsapp-platform-templates"
              element={
                <AdminRoute>
                  <WhatsAppPlatformTemplatesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/channels"
              element={
                <AdminRoute>
                  <ChannelsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/versions"
              element={
                <AdminRoute>
                  <VersionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whatsapp-numbers"
              element={
                <AdminRoute>
                  <WhatsAppNumbersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings/scheduled-jobs"
              element={
                <AdminRoute>
                  <ScheduledJobsSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings/plans-pricing"
              element={
                <AdminRoute>
                  <PlansPricingSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/config-audit"
              element={
                <AdminRoute>
                  <ConfigAuditPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/reminders-today"
              element={
                <AdminRoute>
                  <RemindersTodayPage />
                </AdminRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <BuildBadge />
          <AnalyticsGate />
        </AuthProvider>
      </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  )
}
